import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { MortgageBanner } from '../../../components/Gallery/components/MortgageBanner';
import { EMediaType, TMedia } from '../../../components/Gallery/types';
import { createFinMortgageFrame } from '../../../components/offer_gallery/createFinMortgageFrame';
import { MortgageFrame } from '../../../components/offer_gallery/createMortgageFrame';
import { useIsMortgageBannerAvailable } from '../../../hooks/mortgage/useIsMortgageBannerAvailable';
import { EOfferType } from '../../../repositories/offer-card/entities/offer/OfferSchema';
import { banksListSelector, selectMortgageGallery, selectOffer } from '../../../selectors';
import { selectMortgageRates } from '../../../selectors/mortgage/rates';
import { trackGalleryLastSlideMortgageShow } from '../../../utils/tracking/galleryLastSlideMortgageBanner';
import { getMortgageBannerVerticalName } from '../../../utils/tracking/mortgageUnderPriceCianBanner';

export const useMortgageBanner = (defaultThumbnailSrc: string): TMedia | null => {
  const { mortgageGalleryWidgetMarkup: mortgageBannerMarkup } = useSelector(selectMortgageGallery);
  const mortgageRates = useSelector(selectMortgageRates);
  const banksList = useSelector(banksListSelector);
  const offer = useSelector(selectOffer);
  const { offerType } = offer;
  const isMortgageBannerAvailable = useIsMortgageBannerAvailable();

  const isSuburban = offerType === EOfferType.Suburban;
  const verticalName = React.useMemo(() => getMortgageBannerVerticalName(offer), [offer]);

  return React.useMemo<TMedia | null>(() => {
    if (!isMortgageBannerAvailable) {
      return null;
    }

    let component: React.ReactNode = null;
    let thumbnailSrc = '';
    if (mortgageBannerMarkup) {
      const legacyFotoramaFrame = createFinMortgageFrame(mortgageBannerMarkup);
      component = <MortgageBanner innerHTML={legacyFotoramaFrame.html} verticalName={verticalName} />;
      thumbnailSrc = legacyFotoramaFrame.thumb;
    } else {
      component = (
        <MortgageFrame
          banksList={banksList}
          isSuburban={isSuburban}
          rates={mortgageRates}
          verticalName={verticalName}
        />
      );
      thumbnailSrc = defaultThumbnailSrc;
    }

    const wrapped = (
      <ActionAfterViewObserver
        callback={() => trackGalleryLastSlideMortgageShow(verticalName)}
        callbackDelay={1000}
        wrapper={false}
      >
        {component}
      </ActionAfterViewObserver>
    );

    return {
      type: EMediaType.Component,
      component: wrapped,
      thumbnailSrc,
    };
  }, [
    banksList,
    defaultThumbnailSrc,
    isMortgageBannerAvailable,
    isSuburban,
    mortgageBannerMarkup,
    mortgageRates,
    verticalName,
  ]);
};
